import { useMemo } from "react";
import { limit, orderBy } from "firebase/firestore";
import { useCoupleCollection } from "./useCoupleCollection";
import type { CalendarEvent, ImportantDate, LinkBoard, Note, Todo, WirList } from "../types";

export interface ActivityItem {
  id: string;
  name: string;
  text: string;
  timestamp: number; // ms epoch, for sorting/relative-time display
  link: string;
}

// Merges "who did what" across the app's main collections into one
// chronological feed for the TopBar activity bell — specifically the
// PARTNER's activity, not your own (your own actions aren't news to you).
// Scoped to creation events (and note updates, which don't have a
// separate per-entry "created" concept) from the top-level collections,
// plus comment activity on todos/calendar events — those live in nested
// subcollections, which would need a collection-group query (and matching
// Firestore indexes) to aggregate directly, so instead the Cloud Functions
// that create a comment denormalize lastCommentAt/lastCommentByUid/
// lastCommentByName/lastCommentText onto the *parent* todo/event doc,
// letting this just run a second top-level query (orderBy lastCommentAt)
// — same low-risk pattern as everything else here, no collection-group
// query involved.
//
// Each raw query fetches a few more than needed (10/8 instead of 5) since
// filtering out your own entries shrinks the pool — if you're the more
// active one, a plain limit(5) could easily filter down to nothing.
export function useActivityFeed(coupleId: string | null, meUid: string | null): ActivityItem[] {
  const { items: events } = useCoupleCollection<CalendarEvent>(coupleId, "calendarEvents", orderBy("createdAt", "desc"), limit(10));
  const { items: commentedEvents } = useCoupleCollection<CalendarEvent>(coupleId, "calendarEvents", orderBy("lastCommentAt", "desc"), limit(8));
  const { items: todos } = useCoupleCollection<Todo>(coupleId, "todos", orderBy("createdAt", "desc"), limit(10));
  const { items: commentedTodos } = useCoupleCollection<Todo>(coupleId, "todos", orderBy("lastCommentAt", "desc"), limit(8));
  const { items: lists } = useCoupleCollection<WirList>(coupleId, "lists", orderBy("createdAt", "desc"), limit(10));
  const { items: dates } = useCoupleCollection<ImportantDate>(coupleId, "importantDates", orderBy("createdAt", "desc"), limit(10));
  const { items: notes } = useCoupleCollection<Note>(coupleId, "notes", orderBy("updatedAt", "desc"), limit(10));
  const { items: boards } = useCoupleCollection<LinkBoard>(coupleId, "linkBoards", orderBy("createdAt", "desc"), limit(10));

  return useMemo(() => {
    const out: ActivityItem[] = [];
    for (const ev of events) {
      if (!ev.createdAt || ev.createdByUid === meUid) continue; // still-pending optimistic write, or my own
      out.push({
        id: `cal-${ev.id}`,
        name: ev.createdByName,
        text: `hat „${ev.title}“ im Kalender hinzugefügt`,
        timestamp: ev.createdAt.toMillis(),
        link: `/calendar?date=${ev.startDate}`,
      });
    }
    for (const ev of commentedEvents) {
      if (!ev.lastCommentAt || ev.lastCommentByUid === meUid) continue;
      out.push({
        id: `cal-comment-${ev.id}-${ev.lastCommentAt.toMillis()}`,
        name: ev.lastCommentByName ?? "",
        text: `hat zu „${ev.title}“ kommentiert: „${ev.lastCommentText}“`,
        timestamp: ev.lastCommentAt.toMillis(),
        link: `/calendar?date=${ev.startDate}&comment=${ev.id}`,
      });
    }
    for (const t of todos) {
      if (!t.createdAt || t.createdByUid === meUid) continue;
      out.push({ id: `todo-${t.id}`, name: t.createdByName, text: `hat „${t.text}“ als Todo hinzugefügt`, timestamp: t.createdAt.toMillis(), link: `/todos?open=${t.id}` });
    }
    for (const t of commentedTodos) {
      if (!t.lastCommentAt || t.lastCommentByUid === meUid) continue;
      out.push({
        id: `todo-comment-${t.id}-${t.lastCommentAt.toMillis()}`,
        name: t.lastCommentByName ?? "",
        text: `hat zu „${t.text}“ kommentiert: „${t.lastCommentText}“`,
        timestamp: t.lastCommentAt.toMillis(),
        link: `/todos?comment=${t.id}`,
      });
    }
    for (const l of lists) {
      if (!l.createdAt || l.createdByUid === meUid) continue;
      out.push({ id: `list-${l.id}`, name: l.createdByName, text: `hat die Liste „${l.name}“ angelegt`, timestamp: l.createdAt.toMillis(), link: `/lists/${l.id}` });
    }
    for (const d of dates) {
      if (!d.createdAt || d.createdByUid === meUid) continue;
      out.push({ id: `date-${d.id}`, name: d.createdByName, text: `hat „${d.title}“ bei wichtigen Daten hinzugefügt`, timestamp: d.createdAt.toMillis(), link: `/dates?open=${d.id}` });
    }
    for (const n of notes) {
      if (!n.updatedAt || n.updatedByUid === meUid) continue;
      out.push({ id: `note-${n.id}`, name: n.updatedByName, text: "hat eine Notiz aktualisiert", timestamp: n.updatedAt.toMillis(), link: `/notes?open=${n.id}` });
    }
    for (const b of boards) {
      if (!b.createdAt || b.createdByUid === meUid) continue;
      out.push({ id: `board-${b.id}`, name: b.createdByName, text: `hat die Link-Sammlung „${b.name}“ angelegt`, timestamp: b.createdAt.toMillis(), link: `/links/${b.id}` });
    }
    return out.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
  }, [events, commentedEvents, todos, commentedTodos, lists, dates, notes, boards, meUid]);
}
